import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight, Check, Download } from "lucide-react";
import { EASE, Reveal, MaskedReveal } from "../motion";
import { Section, Wrap, Eyebrow, Logo } from "./Section";
import { IMAGES, FORM_CONFIG, PROPOSAL_PDF_URL } from "../content";
import { usePanelA11y } from "../a11y";
import { scrollToId } from "./Nav";

const FORMATS = ["Video call", "In person", "Phone call"];
const SA_TEL = /^(\+27|0)[\d\s-]{8,13}$/;

const inputCls =
  "w-full bg-white border border-[#242424]/15 rounded-xl px-4 py-3.5 text-sm text-[#242424] placeholder-[#242424]/35 transition-[border-color,box-shadow] duration-300 focus:border-[#B66E50] focus:ring-2 focus:ring-[#B66E50]/15 focus:outline-none";

const FieldWrap = ({ label, error, children, optional }) => (
  <label className="block">
    <span className="flex items-baseline justify-between text-[10px] font-bold uppercase tracking-[0.18em] text-[#242424]/55 mb-2">
      {label}
      {optional && <span className="normal-case tracking-normal font-medium text-[#242424]/35">Optional</span>}
    </span>
    {children}
    {error && (
      <span data-testid={`error-${label.toLowerCase().replace(/\s+/g, "-")}`} className="block text-xs text-[#B66E50] mt-1.5" role="alert">
        {error}
      </span>
    )}
  </label>
);

export function MeetingPanel({ open, onClose }) {
  const [form, setForm] = useState({ name: "", organisation: "", email: "", telephone: "", preferred_date: "", preferred_format: "", message: "" });
  const [errors, setErrors] = useState({});
  const [state, setState] = useState("idle");
  const panelRef = useRef(null);

  const close = () => {
    onClose();
    setTimeout(() => {
      setState("idle");
      setErrors({});
    }, 500);
  };

  usePanelA11y(open, panelRef, close, "[data-testid='field-name']");

  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (errors[k]) setErrors({ ...errors, [k]: null });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (state === "sending" || state === "sent") return;
    const errs = {};
    if (!form.name.trim()) errs.name = "Please share your name.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) errs.email = "Please use a valid email address.";
    const tel = form.telephone.trim();
    if (tel && !SA_TEL.test(tel)) errs.telephone = "A South African number works best, e.g. +27 82 000 0000.";
    setErrors(errs);
    if (Object.keys(errs).length) return;

    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base || !FORM_CONFIG.endpoint) {
      setState("error");
      return;
    }
    setState("sending");
    try {
      const res = await fetch(`${base}${FORM_CONFIG.endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setState("sent");
    } catch {
      setState("error");
    }
  };

  const goToPrivacy = () => {
    close();
    setTimeout(() => scrollToId("trust"), 350);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="meeting-panel-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            onClick={close}
            className="fixed inset-0 z-[95] bg-[#0F0F0F]/60 backdrop-blur-sm"
          />
          <motion.aside
            ref={panelRef}
            data-testid="meeting-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Request a meeting"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.55, ease: EASE }}
            className="fixed top-0 right-0 bottom-0 z-[100] w-full max-w-md bg-[#F5F3EE] text-[#242424] overflow-y-auto shadow-2xl"
          >
            <div className="p-8 sm:p-10">
              <div className="flex items-start justify-between">
                <div>
                  <Eyebrow>Discuss the project</Eyebrow>
                  <h3 className="font-serif text-3xl font-light mt-4 leading-tight">Let's find the right shape together.</h3>
                </div>
                <button
                  data-testid="meeting-panel-close"
                  onClick={close}
                  aria-label="Close panel"
                  className="p-2 -m-2 text-[#242424]/50 hover:text-[#242424] transition-colors duration-300 cursor-pointer"
                >
                  <X size={20} />
                </button>
              </div>

              {state === "sent" ? (
                <motion.div
                  data-testid="meeting-success"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: EASE }}
                  className="mt-14 text-center"
                >
                  <span className="inline-flex w-16 h-16 rounded-full bg-[#6F806E] text-[#F6F0E6] items-center justify-center">
                    <Check size={26} strokeWidth={2.2} />
                  </span>
                  <p className="font-serif text-2xl font-light mt-6">Thank you, {form.name.split(" ")[0]}.</p>
                  <p className="text-sm text-[#242424]/65 leading-relaxed mt-3 max-w-xs mx-auto">
                    Your request has been received. Blank Space Media will be in touch to arrange the conversation, calmly, and without obligation.
                  </p>
                  <button
                    data-testid="meeting-success-close"
                    onClick={close}
                    className="mt-8 bg-[#242424] text-[#F5F3EE] text-[11px] font-bold uppercase tracking-[0.18em] px-8 py-4 rounded-full transition-[background-color,transform] duration-300 hover:bg-[#0F0F0F] hover:-translate-y-0.5 cursor-pointer"
                  >
                    Return to the proposal
                  </button>
                </motion.div>
              ) : (
                <form data-testid="meeting-form" onSubmit={submit} noValidate className="mt-10 space-y-5">
                  <FieldWrap label="Name" error={errors.name}>
                    <input data-testid="field-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" autoComplete="name" aria-required="true" />
                  </FieldWrap>
                  <FieldWrap label="Organisation" optional>
                    <input data-testid="field-organisation" className={inputCls} value={form.organisation} onChange={set("organisation")} placeholder="Your organisation" autoComplete="organization" />
                  </FieldWrap>
                  <FieldWrap label="Email" error={errors.email}>
                    <input data-testid="field-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.co.za" autoComplete="email" aria-required="true" />
                  </FieldWrap>
                  <FieldWrap label="Telephone" optional error={errors.telephone}>
                    <input data-testid="field-telephone" type="tel" className={inputCls} value={form.telephone} onChange={set("telephone")} placeholder="+27 82 000 0000" autoComplete="tel" />
                  </FieldWrap>
                  <div className="grid grid-cols-2 gap-4">
                    <FieldWrap label="Preferred date" optional>
                      <input data-testid="field-date" type="date" className={inputCls} value={form.preferred_date} onChange={set("preferred_date")} min={new Date().toISOString().split("T")[0]} />
                    </FieldWrap>
                    <FieldWrap label="Format" optional>
                      <select data-testid="field-format" className={inputCls} value={form.preferred_format} onChange={set("preferred_format")}>
                        <option value="">Choose…</option>
                        {FORMATS.map((f) => (
                          <option key={f} value={f}>{f}</option>
                        ))}
                      </select>
                    </FieldWrap>
                  </div>
                  <FieldWrap label="Anything we should know?" optional>
                    <textarea data-testid="field-message" rows={3} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} placeholder="A sentence or two is plenty." />
                  </FieldWrap>

                  {state === "error" && (
                    <p data-testid="meeting-error" role="alert" className="text-xs text-[#B66E50] bg-[#B66E50]/10 rounded-lg px-4 py-3">
                      Something interrupted the send, nothing was delivered. Please try once more, or reach us directly by email.
                    </p>
                  )}

                  <button
                    data-testid="meeting-submit"
                    type="submit"
                    disabled={state === "sending"}
                    className="w-full bg-[#FFD400] text-[#0F0F0F] text-xs font-bold uppercase tracking-[0.2em] px-6 rounded-full transition-[background-color,transform,opacity] duration-300 hover:bg-[#E6BE00] hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 cursor-pointer py-4"
                  >
                    {state === "sending" ? "Sending…" : "Request the conversation"}
                  </button>
                  <p className="text-[11px] text-[#242424]/45 leading-relaxed text-center">
                    Your details are used only to arrange this discussion. Nothing more.{" "}
                    <button
                      type="button"
                      data-testid="meeting-privacy-link"
                      onClick={goToPrivacy}
                      className="underline underline-offset-2 decoration-[#242424]/30 hover:text-[#242424] transition-colors duration-300 cursor-pointer"
                    >
                      Read our privacy approach
                    </button>
                  </p>
                </form>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

export default function Closing({ onDiscuss }) {
  return (
    <Section id="discuss" theme="charcoal" className="py-20 sm:py-28 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <img src={IMAGES.sanctuary} alt="" className="w-full h-full object-cover opacity-[0.14]" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#242424]/70 via-[#242424]/55 to-[#242424]/85" />
      </div>
      <Wrap className="relative">
        <div className="max-w-4xl">
          <Reveal>
            <Eyebrow light>The next step</Eyebrow>
          </Reveal>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight mt-6 text-[#F5F3EE]">
            <MaskedReveal lines={["Your vision deserves an", "experience that already", "feels real."]} baseDelay={0.1} />
          </h2>
          <Reveal delay={3}>
            <p className="text-[#F5F3EE]/65 text-base sm:text-lg leading-relaxed mt-8 max-w-xl">
              This proposal is an early demonstration of how we think: strategically, visually and around the people the
              platform must serve. The next step is to shape the final direction together.
            </p>
          </Reveal>
          <Reveal delay={4} className="mt-12 flex flex-wrap items-center gap-x-10 gap-y-5">
            <button
              data-testid="closing-discuss-button"
              onClick={onDiscuss}
              className="group inline-flex items-center gap-3 bg-[#FFD400] text-[#0F0F0F] text-xs font-bold uppercase tracking-[0.2em] px-10 py-5 rounded-full transition-[background-color,transform] duration-300 hover:bg-[#E6BE00] hover:-translate-y-1 cursor-pointer"
            >
              Discuss the Project
              <ArrowUpRight size={15} strokeWidth={2.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <a
              data-testid="closing-download-pdf"
              href={PROPOSAL_PDF_URL}
              download
              className="inline-flex items-center gap-2.5 text-[#F5F3EE]/60 hover:text-[#F5F3EE] text-[11px] font-semibold uppercase tracking-[0.2em] underline underline-offset-8 decoration-[#F5F3EE]/25 transition-colors duration-300 cursor-pointer"
            >
              <Download size={13} strokeWidth={2.5} />
              Download the formal proposal
            </a>
          </Reveal>
        </div>

        <Reveal delay={5}>
          <footer className="mt-20 pt-10 border-t border-[#F5F3EE]/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
            <Logo dark />
            <p className="font-serif italic text-lg text-[#F5F3EE]/60 max-w-md leading-snug">
              Blank Space Media · strategy, identity and digital experiences designed around people.
            </p>
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#F5F3EE]/35">
              Private proposal · Supported by StrateNova
            </p>
          </footer>
        </Reveal>
      </Wrap>
    </Section>
  );
}
